import React, { Fragment, useRef, useState } from "react";
import { SideBarCategoryProps, Category } from "@/utils/types/product";
import { Button } from "@dxp-feed/ui/Button";
import {
  Box,
  Text,
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Checkbox,
} from "@dxp-feed/ui";
import useMediaQuery from "@/utils/hooks/useMediaQuery";
import {
  useAppDispatch,
  useAppSelector,
  useEffect,
  useMemo,
} from "@/utils/hooks/reactUseCustomHooks";
import { setSubcategory } from "@/utils/redux/productsListing";
import { useClearFilters, useHierarchicalMenu } from "@dxp-feed/algolia";
import {
  ARTICAL_CATEGORIES,
  PRODUCT_FILTERS_ATTRIBUTES,
} from "@/utils/constant";
import { algoliaConfig } from "@/services/algolia/config";
import {
  analyticsFilterClickEvent,
  analyticsViewFilterEvent,
} from "@/services/algolia/analytics";
import { setFilters, setPageFilters } from "@/utils/redux/recipes/algoliaSlice";
import { addDataLayerProducts } from "@/helpers/dataLayer";
import {
  GlobalSearchSectionsType,
  TAnalyticsFilterEvent,
} from "@/utils/types/algolia";
import useFilterViewAnalytics from "@/utils/hooks/useFilterViewAnalytics";
import { setFilterData } from "@/utils/redux/fetchOffers";
import { usePathname } from "next/navigation";


const SidebarCategory: React.FC<SideBarCategoryProps> = ({
  isCatVisible = false,
  closeCat,
  type,
  selectedCategory,
  isSearchPage = false,
  onCategoryChange
}) => {
  const {
    items: category,
    refine,
    canRefine,
  } = useHierarchicalMenu({
    limit: 1000,
    attributes: (type === "products"
      ? PRODUCT_FILTERS_ATTRIBUTES
      : ARTICAL_CATEGORIES
    ).map((ele) => ele.attribute),
  });
  const [selectedCat, setSelectedCat] = useState<any>({});

  const categoriesContainerRef = useRef(null);

  useEffect(() => {
    if (selectedCategory) {
      refine(selectedCategory);
    }
  }, [selectedCategory]);

 
  const pathname = usePathname();
  const windowWidth = useMediaQuery();
  const mediaScreen = (windowWidth ?? 0) <= 1023;
  const showSidebarStyle = "left-0 visible delay-600 duration-700 z-[9999999]";
  const fixedLayoutStyle =
    "fixed bg-brandWhite right-0 bottom-0 top-0 w-full h-full overflow-auto invisible";
  const dispatch = useAppDispatch();
  const userSession = useAppSelector((state) => state.gigyaslice); // Select user session from Redux state
  const userId = userSession.uid ?? undefined; // Get user ID from the session
  const hierarchyLevel = useRef<number>(0);
  const { filters } = useAppSelector((state) => state.algoliaDataSlice); // Select algolia session from Redux state
  const { sideBar } = useAppSelector((state) => state.AemArticledataSlice);

  const { refine: clearRefine, canRefine: canRefineHerirachical } =
    useClearFilters({
      includedAttributes: [
        "hierarchicalCategories.lvl0",
        "hierarchicalCategories.lvl1",
      ], // Use clear filters hook with included attributes
    });

  const { itemRefs } = useFilterViewAnalytics({
    items: category,
    containerRef: categoriesContainerRef,
    analyticsPayload: {
      kind: type as GlobalSearchSectionsType,
      index: algoliaConfig.indexNames[type as GlobalSearchSectionsType],
      userToken: userId,
    },
    filterStringCb: (index: number) =>
      `${PRODUCT_FILTERS_ATTRIBUTES[hierarchyLevel.current].attribute}:${category[index]?.value}`,
  });

  function onClickHandler(category: Category) {
    setSelectedCat((prev: any) =>
      prev.value == category.value ? {} : category
    );
    refine(category.value);
    dispatch(setSubcategory(category.value));
    const filtersCount = Math.max(
      0,
      !category?.isRefined ? filters + 1 : filters - 1
    );
    dispatch(setPageFilters(type as GlobalSearchSectionsType));
    dispatch(setFilters(filtersCount));
    dispatch(
      setFilterData({
        filter: "category",
        filterName: category.value,
        filterValue: category.value,
      })
    );
    if (onCategoryChange) {
      const updated = category.value === selectedCat?.value ? null : category;
      onCategoryChange(updated);
    }
    if ((type == "articles" || type == "products") && closeCat) {
      closeCat();
    }

    // Send analytics event for the filter click and view
    const analyticsPayload: TAnalyticsFilterEvent = {
      kind: type as GlobalSearchSectionsType,
      index: algoliaConfig.indexNames[type as GlobalSearchSectionsType],
      filters: [
        `${PRODUCT_FILTERS_ATTRIBUTES[hierarchyLevel.current].attribute}:${category?.value}`,
      ],
      userToken: userId,
    };
    analyticsFilterClickEvent(analyticsPayload);
  }

  const renderCategories = (categories: Category[], level: number = 0) => {
    const filtered = categories.find(
      (item) => Array.isArray(item.data) && item.data.length > 0
    );
    const result = filtered ? [filtered] : categories; 

    return result?.map((category, key) => {
      if (category.data === null) {
        hierarchyLevel.current = level;
        return (
          <Text
            key={category.label + key}
            //@ts-ignore
            ref={(el) => (itemRefs.current[key] = el)}
            data-index={key}
            as="li"
            className="py-[13.5px] lg:py-0 mb-1 lg:mb-2 text-left"
          >
            <Button
              name="link"
              className={`${category.isRefined ? "font-bold" : ""} cursor-pointer flex items-center text-left justify-between w-full lg:inline lg:w-auto rounded-none capitalize leading-6`}
              onClick={() => {
                onClickHandler(category);
                addDataLayerProducts({
                  event: "category_cta",
                  tile_name: category?.label,
                  tile_position: key + 1,
                  page_title: document.title,
                });
              }}
              aria-label={`${category.label}${
                !category.isRefined &&
                type === "products" &&
                pathname !== "/offers"
                  ? ` (${category.count})`
                  : ""
              }${type === "articles" ? ` (${category.count})` : ""}`}
              variant="icon"
              size="none"
            >
              {category.label}{" "}
              {!category.isRefined &&
              type === "products" &&
              pathname !== "/offers"
                ? "(" + category.count + ")"
                : ""}
              {type == "articles" ? "(" + category.count + ")" : ""}
              {mediaScreen && category.value.split(" > ").length <= 2 && (
                <span className="icon-next-arrow text-sm text-grey950"></span>
              )}
            </Button>
          </Text>
        );
      }

      if (category.data && category.data.length > 0) {
        return (
          <Fragment key={category.label + key}>
            {renderCategories(category.data, level + 1)}
          </Fragment>
        );
      }

      return null;
    });
  };
  const updatedCategory = Array.isArray(category)
  ? category.filter((item) => item?.isRefined === true)
  : [];
  const renderedCategories = useMemo(() => {
    return category && category.length > 0 ? renderCategories(type=="products" && pathname?.includes("category")?updatedCategory:category) : null;
  }, [category, mediaScreen]);

  const ClearButton = () => {
    function handleClear() {
      onClickHandler(selectedCat);
      if (type === "products" && canRefineHerirachical) {
        clearRefine();
      }
    }
    const shouldRenderBtn =
      type === "articles" ||
      (type === "products" && mediaScreen && isSearchPage);
    return shouldRenderBtn ? (
      <Button
        variant="icon"
        className={`font-bold px-1 text-[14px] mt-4  text-center text-brandSecondary lg:text-left underline lg:no-underline h-10 flex items-center justify-center lg:justify-start w-full ${
          Object.keys(selectedCat).length === 0
            ? "font-normal bg-transparent p-0 before:bg-transparent hover:bg-transparent"
            : ""
        }`}
        aria-label={"Clear All"}
        disabled={Object.keys(selectedCat).length === 0}
        onClick={handleClear}
      >
        Clear All
      </Button>
    ) : null;
  };

  return (
    <Box
      as="div"
      style={{
        visibility: !mediaScreen || isCatVisible ? "visible" : "hidden",
      }}
      className={`flex flex-col ${
        mediaScreen ? fixedLayoutStyle : ""
      } ${mediaScreen && isCatVisible ? showSidebarStyle : ""} ${
        mediaScreen && !isCatVisible
          ? "left-full z-[-1] delay-600 duration-700"
          : ""
      }`}
    >
      {category && category.length > 0 && 
      (!(type === "products" && pathname?.includes("category")) ||
      (updatedCategory?.[0]?.data &&
        updatedCategory[0].data.length > 0)) && (
        <>
          <Accordion
            className="hidden lg:flex flex-col gap-6 lg:gap-4 border-b border-grey350"
            multiplePanelOpen={true}
            defaultActiveKey={`categoryItem`}
          >
            <AccordionItem id={`categoryItem`}>
              <AccordionHeader
                id={`categoryItem`}
                className="font-bold !px-0 pt-0 pb-2 text-[16px] lg:text-[14px] group [&_.demo-icon]:w-6 [&_.demo-icon]:h-6 [&_.demo-icon]:flex [&_.demo-icon]:items-center [&_.demo-icon]:justify-center"
              >
                {type == "articles" ? sideBar?.categories : "Categories"}
              </AccordionHeader>
              <AccordionPanel
                customRef={categoriesContainerRef}
                id={`categoryItem`}
                className="[&>div]:p-0 [&>div]:flex [&>div]:flex-col [&>div]:gap-2 [&>div]:py-0 [&>div]:pb-2 lg:[&>div]:py-2 [&>div]:border-t [&>div]:border-none lg:[&>div]:border-grey350 [&>div]:overflow-auto [&>div]:max-h-[320px] [&>div]:scroll-bar-grey"
              >
                <Box as="ul" className="text-body pr-4">
                  {renderedCategories}
                </Box>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
          {mediaScreen && (
            <>
              <Box
                as="div"
                className="absolute lg:relative top-0 left-0 right-0 bg-brandWhite lg:bg-transparent lg:hover:bg-transparent lg:focus:bg-transparent z-10 lg:z-0"
              >
                <Box
                  as="div"
                  className="font-bold p-6 lg:p-0 lg:pb-4 lg:mb-0 bg-brandWhite border-b border-grey350 lg:border-none text-[16px] capitalize text-brandBlack"
                >
                  {type == "articles" ? sideBar?.categories : "Categories"}
                </Box>
                <Button
                  className="absolute right-[24px] top-[25px] flex cursor-pointer text-grey950 p-0 bg-transparent hover:bg-transparent focus:bg-transparent border-0"
                  onClick={closeCat}
                  aria-label="Close"
                  tabIndex={0}
                >
                  <span className="icon-close text-body"></span>
                </Button>
              </Box>
              <Box
                as="div"
                className="px-6 pt-[100px] pb-[10px] lg:p-0 overflow-y-scroll !overflow-x-hidden lg:max-h-auto lg:overflow-y-auto"
              >
                <Text as="ul" className="text-body pr-4">
                  {renderedCategories}
                </Text>
              </Box>
            </>
          )}
        </>
      )}
      <ClearButton />
    </Box>
  );
};

export default SidebarCategory;